import React, { useRef, useState, useEffect } from 'react';
import './GameSteps.css'; // Import your CSS file for styling
import GameContentProvider from '../providers/GameContentProvider';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import Confetti from 'react-confetti';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import Toggle from '../Toggle';
import IntroGuideOverlay from './IntroGuideOverlay';
import { FaMapMarkedAlt, FaLightbulb, FaFastForward, FaPause, FaPlay, FaImages, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';

const loadGoogleMaps = (callback) => {
    // Detect environment based on hostname
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    // Use the appropriate API key
    const apiKey = isLocalhost
        ? "AIzaSyCLltv9TtdzLnNWKwgpN7eG10IlKK674uQ"    // Localhost API Key
        : "AIzaSyATNBFtqIHepH9en1I-sU80qnMBywHI7TI";
    // Production API Key

    if (typeof window.google === 'undefined' || typeof window.google.maps === 'undefined') {
        const existingScript = document.getElementById('googleMaps');
        if (!existingScript) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.id = 'googleMaps';
            script.async = true;
            script.defer = true;

            script.onload = () => {
                if (window.google && window.google.maps) {
                    callback();
                } else {
                    const interval = setInterval(() => {
                        if (window.google && window.google.maps) {
                            clearInterval(interval);
                            callback();
                        }
                    }, 100);
                }
            };

            document.body.appendChild(script);
        } else {
            existingScript.onload = () => {
                if (window.google && window.google.maps) {
                    callback();
                }
            };
        }
    } else if (callback) {
        callback();
    }
};



const MapOverlay = ({ closeOverlay, recommendations, city, step, hintOpened }) => {
    console.log("MapOverlay received step:", step);
    if (step) {
        console.log("show_step_regions:", step.show_step_regions, "lat:", step.location_latitude, "lng:", step.location_longitude, "hintOpened:", hintOpened);
    }
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [place, setPlace] = useState(null); // To store the searched place
    const [coordinates, setCoordinates] = useState(null);
    const [error, setError] = useState(null); // To store any errors
    const mapRef = useRef(null); // Ref to store the Google Map object
    const mapInstance = useRef(null);
    const searchBoxRef = useRef(null); // Ref for the SearchBox input
    const [showMarkersSelection, setShowMarkersSelection] = useState(false);
    const [toggleStates, setToggleStates] = useState([]);
    const markersRef = useRef([]);
    const searchMarkersRef = useRef([]);
    const watchIdRef = useRef(null);
    const [userLocation, setUserLocation] = useState(null);
    const [showLocationOverlay, setShowLocationOverlay] = useState(false);
    const targetCircleRef = useRef(null);
    const autoFitDoneRef = useRef(false);

    const rows = [
        { id: 1, image: "/assets/marker_grab_and_go.png", text: t('grab_go') },
        { id: 2, image: "/assets/marker_restaurants2.png", text: t('restaurants') },
        { id: 3, image: "/assets/marker_games.png", text: t('activities') },
        { id: 4, image: "/assets/marker_information.png", text: t('information') },
        { id: 5, image: "/assets/marker_bar.png", text: t('bar') },
    ];

    const handleToggleChange = (index, newValue) => {
        const updatedStates = [...toggleStates];
        updatedStates[index] = newValue;
        setToggleStates(updatedStates);
        localStorage.setItem('toggleStates', JSON.stringify(updatedStates));

        updateMarkers();
    };

    const sofiaBounds = { north: 42.8400, south: 42.6000, east: 23.4200, west: 23.1000 };
    const sofiaLat = 42.6977, sofiaLong = 23.3219;

    const varnaBounds = { north: 43.2500, south: 43.1500, east: 28.0500, west: 27.8500 };
    const varnaLat = 43.2044, varnaLong = 27.9174;

    const plovdivBounds = { north: 42.1800, south: 42.1000, east: 24.8000, west: 24.6000 };
    const plovdivLat = 42.1354, plovdivLong = 24.7453;

    let bounds, centerLat, centerLong;
    if (city === "sofia") {
        bounds = sofiaBounds;
        centerLat = sofiaLat;
        centerLong = sofiaLong;
    } else if (city === "varna") {
        bounds = varnaBounds;
        centerLat = varnaLat;
        centerLong = varnaLong;
    } else if (city === "plovdiv") {
        bounds = plovdivBounds;
        centerLat = plovdivLat;
        centerLong = plovdivLong;
    }

    useEffect(() => {
        loadGoogleMaps(() => {
            initializeMap(); // Ensure the map is initialized

            if (mapInstance.current) {
                console.log("Google Maps loaded, starting location tracking...");
                startTrackingLocation();
            } else {
                console.error("Map instance is not ready yet.");
            }
        });

        // Cleanup function: Stop watching and remove markers/circles when component unmounts
        return () => {
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
            // Explicitly remove location markers from the map
            if (markersRef.current.currentLocationMarker) {
                markersRef.current.currentLocationMarker.setMap(null);
                markersRef.current.currentLocationMarker = null;
            }
            if (markersRef.current.accuracyCircle) {
                markersRef.current.accuracyCircle.setMap(null);
                markersRef.current.accuracyCircle = null;
            }
        };
    }, []);


    const initializeMap = () => {
        if (typeof window.google !== 'undefined') {
            if (!mapInstance.current) {
                mapInstance.current = new window.google.maps.Map(mapRef.current, {
                    center: { lat: centerLat, lng: centerLong },
                    zoom: 15,
                    mapTypeControl: false,
                    zoomControl: true,
                    fullscreenControl: true,
                    streetViewControl: false,
                    styles: [
                        {
                            featureType: "poi.business",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "transit",
                            elementType: "labels.icon",
                            stylers: [{ visibility: "off" }]
                        }
                    ]
                });
            }

            // Draw target region if enabled or hint opened (if game supports regions)
            const shouldShowCircle = step && step.location_latitude && step.location_longitude && (step.show_step_regions || (hintOpened && step.show_step_regions));
            // Wait, logic says: ONLY if they open hint AND show_step_regions = 1.
            // Option B: The circle is never visible initially. It ONLY appears if they open the hint AND show_step_regions = 1.
            const showCircle = step && step.location_latitude && step.location_longitude && step.show_step_regions && hintOpened;

            if (showCircle) {
                const targetLat = parseFloat(step.location_latitude);
                const targetLng = parseFloat(step.location_longitude);

                if (!targetCircleRef.current) {
                    targetCircleRef.current = new window.google.maps.Circle({
                        strokeColor: "#FF9800",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#FF9800",
                        fillOpacity: 0.25,
                        map: mapInstance.current,
                        center: { lat: targetLat, lng: targetLng },
                        radius: 100 // 100 meters
                    });
                } else {
                    targetCircleRef.current.setCenter({ lat: targetLat, lng: targetLng });
                }
            } else if (targetCircleRef.current) {
                targetCircleRef.current.setMap(null);
                targetCircleRef.current = null;
            }

            const savedStates = JSON.parse(localStorage.getItem('toggleStates'));
            setToggleStates(savedStates && Array.isArray(savedStates) ? savedStates : [true, true, true, true]);

            console.log(city);
            // Initialize search box
            const input = document.getElementById('search-box');
            if (input && window.google.maps.places) {
                searchBoxRef.current = new window.google.maps.places.SearchBox(input);
                searchBoxRef.current.setBounds(bounds);

                searchBoxRef.current.addListener("places_changed", () => {
                    const places = searchBoxRef.current.getPlaces();
                    if (places && places.length > 0) {
                        const place = places[0];
                        setSearchQuery(place.name);
                        if (place.geometry && place.geometry.location) {
                            setPlace(place.formatted_address);
                            setCoordinates({
                                lat: place.geometry.location.lat(),
                                lng: place.geometry.location.lng()
                            });
                        }
                    }
                });

                console.log("Set bounds listener");
                mapInstance.current.addListener('bounds_changed', () => {
                    searchBoxRef.current.setBounds(mapInstance.current.getBounds());
                });
            }
        }
    };



    // Function to update markers
    const updateMarkers = () => {
        if (!mapInstance.current || typeof window.google === 'undefined' || !window.google.maps) return;

        // Create custom icons for different recommendation types
        const icons = {
            1: { url: '/assets/marker_grab_and_go.png', scaledSize: new window.google.maps.Size(35, 50) },
            2: { url: '/assets/marker_restaurants2.png', scaledSize: new window.google.maps.Size(35, 50) },
            3: { url: '/assets/marker_games.png', scaledSize: new window.google.maps.Size(35, 50) },
            4: { url: '/assets/marker_information.png', scaledSize: new window.google.maps.Size(35, 50) },
            5: { url: '/assets/marker_bar.png', scaledSize: new window.google.maps.Size(35, 50) },
        };

        // Remove existing markers
        const map = mapInstance.current;

        // Remove existing recommendation markers from map
        markersRef.current.forEach((marker) => {
            if (marker && marker.setMap) marker.setMap(null);
        });
        // Filter out the indexed recommendation markers but keep the location properties
        markersRef.current = markersRef.current.filter(() => false);

        // Add markers based on recommendations and toggleStates
        if (recommendations && recommendations.length > 0) {
            recommendations.forEach((rec) => {
                if (toggleStates[rec.type - 1]) {
                    const icon = icons[rec.type] || icons[1];
                    const marker = new window.google.maps.Marker({
                        map: map,
                        position: { lat: rec.latitude, lng: rec.longitude },
                        icon: icon,
                        title: rec.name,
                    });

                    const infowindow = new window.google.maps.InfoWindow({
                        content: `<b>${rec.name}</b><br>${rec.description}`,
                    });

                    marker.addListener('click', () => {
                        infowindow.open({
                            anchor: marker,
                            map: map,
                            shouldFocus: false,
                        });
                    });

                    markersRef.current.push(marker);
                }
            });
        }
    };


    const clearSearchMarkers = () => {
        if (searchMarkersRef.current && searchMarkersRef.current.length > 0) {
            searchMarkersRef.current.forEach((marker) => {
                if (marker && marker.setMap) {
                    marker.setMap(null); // Remove marker from the map
                }
            });
            searchMarkersRef.current = []; // Clear the array
        }

    };



    useEffect(() => {
        if (!mapInstance.current || !window.google || typeof window.google === 'undefined') return;

        if (coordinates) {
            console.log("Setting center:", coordinates);

            // Clear existing search markers
            clearSearchMarkers();

            // Set the new center of the map
            mapInstance.current.setCenter(coordinates);

            // Add a new marker at the coordinates
            const newMarker = new window.google.maps.Marker({
                position: coordinates,
                map: mapInstance.current,
            });

            // Store the new marker in the searchMarkersRef
            searchMarkersRef.current.push(newMarker);
            console.log("New marker added:", newMarker);
        }
    }, [coordinates]);

    // Effect to update markers when toggleStates or recommendations change
    useEffect(() => {
        updateMarkers();
    }, [recommendations, toggleStates]); // Only re-run this effect when these dependencies change

    const startTrackingLocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by this browser.");
            return;
        }

        if (!window.google || !window.google.maps) {
            console.error("Google Maps API not loaded yet.");
            return;
        }

        if (watchIdRef.current !== null) return; // Prevent multiple watchers

        watchIdRef.current = navigator.geolocation.watchPosition(
            (position) => {
                const newLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };

                setUserLocation(newLocation);

                // Ensure the map instance exists
                if (!mapInstance.current) return;

                // Initialize marker if not exists
                if (!markersRef.current.currentLocationMarker) {
                    markersRef.current.currentLocationMarker = new window.google.maps.Marker({
                        position: newLocation,
                        map: mapInstance.current,
                        icon: {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: '#4285F4',
                            fillOpacity: 1,
                            strokeColor: 'white',
                            strokeWeight: 2,
                            scale: 8,
                        },
                        zIndex: 1000,
                        title: "You are here",
                    });
                } else {
                    markersRef.current.currentLocationMarker.setPosition(newLocation);
                }

                // Initialize accuracy circle if not exists
                if (!markersRef.current.accuracyCircle) {
                    markersRef.current.accuracyCircle = new window.google.maps.Circle({
                        strokeColor: '#4285F4',
                        strokeOpacity: 0.4,
                        strokeWeight: 1,
                        fillColor: '#4285F4',
                        fillOpacity: 0.15,
                        map: mapInstance.current,
                        center: newLocation,
                        radius: position.coords.accuracy,
                    });
                } else {
                    markersRef.current.accuracyCircle.setCenter(newLocation);
                    markersRef.current.accuracyCircle.setRadius(position.coords.accuracy);
                }

                // Auto-fit bounds logic: Once per map load, if we have a target circle, fit bounds to show both user and target
                if (!autoFitDoneRef.current && targetCircleRef.current) {
                    const bounds = new window.google.maps.LatLngBounds();
                    bounds.extend(newLocation);

                    // The circle has bounds too, we can extend by its bounds
                    const circleBounds = targetCircleRef.current.getBounds();
                    if (circleBounds) {
                        bounds.union(circleBounds);
                    } else {
                        bounds.extend({ lat: parseFloat(step.location_latitude), lng: parseFloat(step.location_longitude) });
                    }

                    mapInstance.current.fitBounds(bounds);

                    // Add a slight padding out to zoom out a bit for better overview
                    const zoom = mapInstance.current.getZoom();
                    if (zoom) {
                        mapInstance.current.setZoom(zoom - 1);
                    }

                    autoFitDoneRef.current = true;
                }
            },
            (error) => {
                console.error("Error getting location:", error);
                setShowLocationOverlay(true);
            },
            { enableHighAccuracy: true }
        );
    };


    const centerMapOnUser = () => {
        if (userLocation && mapInstance.current) {
            mapInstance.current.setCenter(userLocation);
            mapInstance.current.setZoom(15);
        }
    };




    const fetchPlace = async (query) => {
        console.log(query);
        if (!window.google || !window.google.maps) {
            setError("Google Maps API not loaded properly.");
            return;
        }

        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ address: query }, (results, status) => {
            if (status === window.google.maps.GeocoderStatus.OK) {
                console.log(results);
                const location = results[0].geometry.location;
                setPlace(results[0].formatted_address);
                setCoordinates({ lat: location.lat(), lng: location.lng() });

                setError(null);
            } else {
                setPlace(null);
                setCoordinates(null);
                setError("Place not found.");
            }
        });
    };

    const handleSearchClick = () => {
        console.log("handleSearchClick");
        if (searchQuery) {
            fetchPlace(searchQuery); // Fetch the place when the button is clicked
        }
    };

    const closeMap = () => {
        exitFullScreen();

        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
        }

        closeOverlay('map');


    };

    const exitFullScreen = () => {
        // 1. Check if the browser is CURRENTLY in full-screen mode
        const isFullScreen = document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement;

        // 2. Only attempt to exit if we are actually in full-screen
        if (isFullScreen) {
            if (document.exitFullscreen) {
                // Catch any lingering promise rejections (standard in modern browsers)
                document.exitFullscreen().catch(err => console.warn("Fullscreen exit error:", err));
            } else if (document.mozCancelFullScreen) { // Firefox
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) { // Chrome, Safari, Opera
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { // IE/Edge
                document.msExitFullscreen();
            }
        }
    };







    return (
        <div style={{ background: 'white', height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>

            {/* Search Bar & Controls */}
            <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '10px',
                background: 'white',
                boxSizing: 'border-box',
                gap: '8px' /* Adds consistent spacing between all items */
            }}>
                <input
                    id="search-box"
                    type="text"
                    placeholder="Search for streets or places..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        padding: '8px',
                        flex: 1, /* Takes up all remaining space instead of a fixed 60% */
                        minWidth: '0' /* Prevents flexbox from blowing out the screen width */
                    }}
                />
                <button
                    onClick={handleSearchClick}
                    style={{ padding: '8px', flexShrink: 0 }} /* flexShrink: 0 prevents squishing */
                >
                    {t('search')}
                </button>

                <div className="iconMylocation" onClick={centerMapOnUser} style={{ flexShrink: 0, cursor: 'pointer' }} />
                <div className="iconMarkers" onClick={() => setShowMarkersSelection(true)} style={{ flexShrink: 0, cursor: 'pointer' }} />
            </div>

            {/* Map Container */}
            <div style={{ flex: 1, overflow: 'hidden', width: '100%' }}>
                <div id="map" ref={mapRef} style={{ height: '100%', width: '100%' }}></div>
            </div>

            {/* Close Button */}
            <button className="button" onClick={() => closeMap()}>
                {t("close")}
            </button>

            {/* Marker Selection Overlay */}
            {showMarkersSelection && (
                <div className='overlay-steps'>
                    <div className='overlay-content'>
                        <h3>{t("chose_markers")}</h3>
                        <div>
                            {rows.map((row, index) => (
                                <div
                                    key={row.id}
                                    style={{ display: 'flex', padding: '5px', marginBottom: '5px', alignItems: 'center' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <img
                                            src={row.image}
                                            alt={row.text}
                                            style={{ width: '35px', height: '50px', borderRadius: '8px', objectFit: 'cover', marginRight: '10px' }}
                                        />
                                        <h4>{row.text}</h4>
                                    </div>
                                    <div style={{ position: 'absolute', left: '57%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center' }}>
                                        <Toggle
                                            checked={toggleStates[index]}
                                            onChange={(newValue) => handleToggleChange(index, newValue)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setShowMarkersSelection(false)}> {t("close")}</button>
                    </div>
                </div>
            )}

            {/* Location Permission Overlay */}
            {showLocationOverlay && (
                <div className='overlay-steps'>
                    <div className='overlay-content'>
                        <h3>{t("location_permission.title")}</h3>
                        <p>{t("location_permission.description")}</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button onClick={() => setShowLocationOverlay(false)}>
                                {t("location_permission.close")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );

};







const GameSteps = () => {
    const { t, i18n } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [overlayType, setOverlayType] = useState(null);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const hasTimedOut = useRef(false);

    const [step, setStep] = useState(null);
    const [gameId, setGameId] = useState(null);
    const [stepId, setStepId] = useState(null);
    const [playId, setPlayId] = useState(null);
    const [imagePath, setImagePath] = useState('');
    const [showIntroGuide, setShowIntroGuide] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [skipNextData, setSkipNextData] = useState(null);
    const audioInstanceRef = useRef(null); // JS Audio() object, created on demand
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [carouselVisible, setCarouselVisible] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(1);
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);
    const [hintOpened, setHintOpened] = useState(false);


    const navigate = useNavigate();
    const location = useLocation();


    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };

    // Add this near your other state variables in GameSteps
    const [windowDimension, setWindowDimension] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const detectSize = () => {
            setWindowDimension({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', detectSize);
        return () => window.removeEventListener('resize', detectSize);
    }, []);

    const handleIconClick = (overlay) => {
        if (overlay === "map") {
            enterFullScreen();
        }

        if (overlay === 'hints' && !hintOpened) {
            if (step.hint_used_this_step) {
                setHintOpened(true);
                setOverlayType('hints');
                setOverlayVisible(true);
                return;
            }
            setOverlayType('hint_confirm');
            setOverlayVisible(true);
            return;
        }

        // Handle icon click logic
        // For example, you can set the overlay type and visibility here
        setOverlayType(overlay);
        setOverlayVisible(true);
    };

    const handlePauseFromCorrect = async (overlay) => {
        const gameContentProvider = new GameContentProvider();
        let token = localStorage.getItem('token') || localStorage.getItem('guest_token');

        // 1. Pause the game (DO NOT mark step as completed yet)
        const pauseResult = await gameContentProvider.pauseGame(playId, token, overlay);
        if (pauseResult && pauseResult.success) {
            setIsPaused(true);
            setOverlayType('paused');
            setOverlayVisible(true);

            // Update local step state with pause data
            if (pauseResult.data) {
                setStep(prev => ({
                    ...prev,
                    is_paused: 1,
                    paused_at: pauseResult.data.paused_at,
                    paused_overlay: overlay
                }));
            }
        }
    };

    const handleResume = async () => {
        const gameContentProvider = new GameContentProvider();
        let token = localStorage.getItem('token') || localStorage.getItem('guest_token');
        const result = await gameContentProvider.resumeGame(playId, token);
        if (result && result.success) {
            setIsPaused(false);

            // Update local step state with resume data (new start_date and end_date)
            if (result.data) {
                setStep(prev => ({
                    ...prev,
                    is_paused: 0,
                    paused_at: null,
                    paused_overlay: null,
                    start_date: result.data.start_date,
                    end_date: result.data.end_date
                }));
            }

            // Trigger an immediate check to see if the game has advanced while paused/reloaded
            const currentServerStepData = await gameContentProvider.getCurrentStep(playId, token);
            if (currentServerStepData && Number(currentServerStepData[0]) > Number(stepId)) {
                // Refresh step details to ensure everything is up to date
                const stepsDetails = await gameContentProvider.getStepDetails(gameId, stepId, (i18n.language || 'bg').substring(0, 2), playId);
                console.log(stepsDetails);
                if (stepsDetails && stepsDetails[0]) {
                    setStep(stepsDetails[0]);
                }

                setSyncTargetStep(currentServerStepData[0]);
                setOverlayType('sync');
                setOverlayVisible(true);
                return; // Stop here, the user will proceed via the sync overlay
            }

            // Check if we were on a specific overlay before pausing
            const previousOverlay = step?.paused_overlay;
            if (previousOverlay) {
                // Automatically proceed to next step/results if we paused from an answer overlay
                if (previousOverlay === "correct" || previousOverlay === "final") {
                    closeOverlay(previousOverlay);
                    return; // closeOverlay handles navigation/refresh
                }
                // Only restore 'skipped' overlay if THIS device has skipNextData
                // (i.e., this is the phone that actually did the skip).
                // On phone 2, skipNextData is null — just close the paused overlay and resume.
                if (previousOverlay === 'skipped' && !skipNextData) {
                    setOverlayVisible(false);
                    setOverlayType(null);
                } else {
                    setOverlayType(previousOverlay);
                    setOverlayVisible(true);
                }
            } else {
                setOverlayVisible(false);
                setOverlayType(null);
            }

            // Refresh step details to get the new end_date
            const stepsDetails = await gameContentProvider.getStepDetails(gameId, stepId, (i18n.language || 'bg').substring(0, 2), playId);
            if (stepsDetails && stepsDetails[0]) {
                setStep(stepsDetails[0]);
            }
        }
    };

    const enterFullScreen = () => {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) { // Firefox
            document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) { // Chrome, Safari, Opera
            document.documentElement.webkitRequestFullscreen();
        } else if (document.documentElement.msRequestFullscreen) { // IE/Edge
            document.documentElement.msRequestFullscreen();
        }
    };



    const skipStep = async () => {
        // Close the current overlay
        setOverlayVisible(false);

        console.log("Skip step " + step.step);

        const newData = {
            game: gameId,
            step: Number(stepId) + 1,
            play_id: playId,
        };

        const gameContentProvider = new GameContentProvider();
        let token;
        token = localStorage.getItem('token');
        if (!token) {
            token = localStorage.getItem('guest_token');
        }

        const result = await gameContentProvider.skipStep(stepId, token, playId);
        console.log(result);
        //Clear value
        setInputValue("");

        // Instead of navigating directly, show the 'skipped' intermediate overlay
        // Store next step data so the user can continue when ready
        setSkipNextData(newData);
        setOverlayType('skipped');
        setOverlayVisible(true);
    }

    const toggleAudio = () => {
        if (!step?.audio_base) return;

        const src = `/assets/audio/${step.audio_base}_${(i18n.language || 'bg').substring(0, 2)}_${step.step}.mp3`;

        // Create the Audio instance on first play
        if (!audioInstanceRef.current) {
            audioInstanceRef.current = new Audio(src);
            audioInstanceRef.current.onended = () => setIsAudioPlaying(false);
            audioInstanceRef.current.onerror = (e) => {
                console.error('Audio load error:', e, 'src:', src);
                setIsAudioPlaying(false);
            };
        }

        if (isAudioPlaying) {
            audioInstanceRef.current.pause();
            setIsAudioPlaying(false);
        } else {
            audioInstanceRef.current.play()
                .then(() => setIsAudioPlaying(true))
                .catch(err => {
                    console.error('Audio play() rejected:', err, 'src:', src);
                    setIsAudioPlaying(false);
                });
        }
    };

    const stopAudio = () => {
        if (audioInstanceRef.current) {
            audioInstanceRef.current.pause();
            audioInstanceRef.current.currentTime = 0;
            audioInstanceRef.current = null; // discard so a new instance is created next time
            setIsAudioPlaying(false);
        }
    };

    const closeOverlay = async (overlay) => {
        stopAudio();
        // Close the overlay
        setOverlayVisible(false);
        setOverlayType(null);

        if (overlay == "correct" || overlay == "final") {
            //Start next step
            const newData = {
                game: gameId,
                step: Number(stepId) + 1,
                play_id: playId,
            };

            //Report step has been completed
            const gameContentProvider = new GameContentProvider();
            let token;
            token = localStorage.getItem('token');
            if (!token) {
                console.log("kur2");
                token = localStorage.getItem('guest_token');
            }

            console.log("Current token: " + token);
            const result = await gameContentProvider.stepCompleted(stepId, token, playId);

            if (result) {
                console.log("Open next step for: ", newData);

                if (overlay == "correct") {
                    //Clear value
                    setInputValue("");

                    //Not final step. Open next step
                    navigate(location.pathname, { state: newData, replace: true });
                } else {
                    //Final step
                    navigate("/game-overview", { state: newData, replace: true });
                }


            } else {
                console.log("Error completing the step");
            }

        }
    };

    const handleNextStep = (overlay) => {
        console.log(inputValue);
        console.log(step.answer);


        if (!step.answer.some(item => item.toLowerCase().trim() === inputValue.toLowerCase().trim())) {
            //Code is incorrect
            handleIconClick('wrong');
        } else if (step.finalStep == 1) {
            // This is the last step
            //Start screen 
            handleIconClick('final');
            console.log("Final Step - Congratulations");
        } else {
            handleIconClick('correct');

        }
    };

    useEffect(() => {
        const handleMessage = (event) => {
            const data = event.data;
            if (data && data.game && data.step) {
                setGameId(data.game);
                setStepId(data.step);
                setPlayId(data.play_id);

                // Store the data in sessionStorage
                sessionStorage.setItem('game', data.game);
                sessionStorage.setItem('step', data.step);
                sessionStorage.setItem('play_id', data.play_id);

                console.log('game:', data.game);
                console.log('step:', data.step);
                console.log('play_id:', data.play_id);
            }
        };

        // Load data from sessionStorage when the page loads
        const storedGameId = sessionStorage.getItem('game');
        const storedStepId = sessionStorage.getItem('step');
        const storedPlayId = sessionStorage.getItem('play_id');

        if (storedGameId && storedStepId && storedPlayId) {
            setGameId(storedGameId);
            setStepId(storedStepId);
            setPlayId(storedPlayId);
        }

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);



    useEffect(() => {
        const { game, step, play_id, type } = location.state || {};
        if (game) {
            setGameId(game);
        }
        if (step) {
            setStepId(step);
            console.log("Step: " + step);
        }

        if (play_id) {
            setPlayId(play_id);
        }

    }, [location.state]);


    useEffect(() => {
        // Reset states when changing steps to prevent flickering old data
        setStep(null);
        setImagePath('');
        setInputValue('');
        setOverlayVisible(false);
        setOverlayType(null);
        setHintOpened(false);
        hasTimedOut.current = false;

        const fetchData = async () => {
            try {
                const gameContentProvider = new GameContentProvider();
                const stepsDetails = await gameContentProvider.getStepDetails(gameId, stepId, (i18n.language || 'bg').substring(0, 2), playId);
                console.log(stepsDetails);
                setStep(stepsDetails[0]);
                if (stepsDetails[0].is_paused) {
                    setIsPaused(true);
                    setOverlayType('paused');
                    setOverlayVisible(true);
                }
                //setImage(stepsDetails[0].image);

                if (stepId == 1 && !localStorage.getItem(`intro_shown_${playId}`)) {
                    setShowIntroGuide(true);
                }
            } catch (error) {
                console.error('Error fetching game details:', error);
                // Handle error as needed
            }

        };

        if (gameId != null && gameId != undefined && stepId != null && stepId != undefined && playId != null && playId != undefined) {
            fetchData();
        }

    }, [gameId, stepId, playId, i18n.language]);

    const [syncTargetStep, setSyncTargetStep] = useState(null);
    useEffect(() => {
        if (!playId || !stepId) return;

        const checkSync = async () => {
            // Skip entirely only when navigation is already being handled
            // ('final' = game over, 'sync' = teammate-advanced overlay already shown)
            // NOTE: 'paused' is NOT here — we must keep polling so the resume can be detected
            // NOTE: 'skipped' is NOT here — see step-advancement guard below
            if (overlayType === 'final' || overlayType === 'sync') return;

            try {
                const gameContentProvider = new GameContentProvider();
                let token = localStorage.getItem('token') || localStorage.getItem('guest_token');

                const syncResult = await gameContentProvider.getCurrentStep(playId, token);
                if (!syncResult) return; // null = network error, skip silently

                const [currentServerStep, gameCompleted, serverIsPaused, serverPausedOverlay] = syncResult;

                if (gameCompleted) {
                    setOverlayType('final');
                    setOverlayVisible(true);
                    return;
                }

                // Sync pause state
                // We use a strict check for serverIsPaused to avoid resuming on undefined/null
                const isServerPausedBool = serverIsPaused === 1 || serverIsPaused === true;

                if (isServerPausedBool && overlayType !== 'paused') {
                    // Server says paused AND we're not already showing the paused overlay — show it.
                    // NOTE: We check overlayType, NOT isPaused, because isPaused may already be true
                    // from a previous poll that set the flag but failed to render the overlay.
                    console.log("Server indicates game is paused. Showing overlay. overlayType was:", overlayType);
                    setIsPaused(true);
                    setOverlayType('paused');
                    setOverlayVisible(true);

                    // Refresh data to get the paused_at timestamp so the timer freezes
                    const stepsDetails = await gameContentProvider.getStepDetails(gameId, stepId, (i18n.language || 'bg').substring(0, 2), playId);
                    if (stepsDetails && stepsDetails[0]) {
                        const updatedStep = { ...stepsDetails[0] };
                        updatedStep.is_paused = 1;
                        if (!updatedStep.paused_at) {
                            updatedStep.paused_at = new Date().toISOString();
                        }
                        setStep(updatedStep);
                    }
                } else if (!isServerPausedBool && isPaused && overlayType === 'paused' && serverIsPaused !== undefined && serverIsPaused !== null) {
                    // Only resume if server explicitly says it's not paused (0 or false)
                    setIsPaused(false);

                    // Check if we were on a specific overlay before pausing
                    const previousOverlay = serverPausedOverlay || step?.paused_overlay;
                    if (previousOverlay === "correct" || previousOverlay === "final") {
                        closeOverlay(previousOverlay);
                        return; // closeOverlay handles navigation/refresh
                    }
                    // Only restore 'skipped' overlay if THIS device has skipNextData.
                    // Phone 2 (synced device) has null skipNextData — just close the paused overlay.
                    if (previousOverlay === 'skipped' && !skipNextData) {
                        setOverlayVisible(false);
                        setOverlayType(null);
                    } else if (previousOverlay) {
                        setOverlayType(previousOverlay);
                        setOverlayVisible(true);
                    } else {
                        setOverlayVisible(false);
                        setOverlayType(null);
                    }

                    // Refresh data to get potentially updated end_date
                    const stepsDetails = await gameContentProvider.getStepDetails(gameId, stepId, (i18n.language || 'bg').substring(0, 2), playId);
                    if (stepsDetails && stepsDetails[0]) {
                        setStep(stepsDetails[0]);
                    }
                }

                // Step advancement check — only run when the game is active (not paused)
                // and the user is not on the 'skipped' overlay (they triggered the advance themselves
                // but haven't navigated yet — they may want to pause first).
                const isEffectivelyPaused = overlayType === 'paused' || isPaused;
                if (!isEffectivelyPaused && overlayType !== 'skipped' && Number(currentServerStep) > Number(stepId)) {
                    setSyncTargetStep(currentServerStep);
                    exitFullScreen();
                    setInputValue("");
                    setOverlayType('sync');
                    setOverlayVisible(true);
                }
            } catch (error) {
                console.error('Error syncing game state:', error);
            }
        };

        const syncInterval = setInterval(checkSync, 10000);
        return () => clearInterval(syncInterval);

    }, [gameId, stepId, playId, location.pathname, overlayType, isPaused]); // Added isPaused to dependencies

    const exitFullScreen = () => {
        const isFullScreen = document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement;

        if (isFullScreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(err => console.warn("Fullscreen exit error:", err));
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    useEffect(() => {
        const updateImagePath = () => {
            if (!step || !step.image) {
                // Prevent updates if step or step.image is not available
                setImagePath('');
                return;
            }

            const orientation = window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
            const folderPath = `../assets/steps/${orientation}`;
            setImagePath(`${folderPath}/${step.image}`);
        };

        // Set initial image path
        updateImagePath();

        // Add an event listener to handle orientation changes
        const handleOrientationChange = () => {
            //console.log("Orienation changed");
            if (step && step.image) {
                // Only update if step and step.image are available
                updateImagePath();
            }
        };

        window.addEventListener('resize', handleOrientationChange);

        // Cleanup the event listener
        return () => window.removeEventListener('resize', handleOrientationChange);
    }, [step]);

    const [showRemaining, setShowRemaining] = useState(false);
    const [elapsedTime, setElapsedTime] = useState('');
    const [remainingTime, setRemainingTime] = useState('');

    useEffect(() => {
        if (!step || !step.start_date || !step.end_date) return;

        const interval = setInterval(() => {
            // Use local isPaused state as well as step data for immediate response
            const effectivelyPaused = isPaused || step.is_paused === 1 || step.is_paused === true;
            const now = effectivelyPaused ? new Date(step.paused_at || new Date()) : new Date();
            const start = new Date(step.start_date);
            const end = new Date(step.end_date);

            if (new Date().getSeconds() % 5 === 0) {
                console.log("Timer State:", { isPaused, "step.is_paused": step.is_paused, effectivelyPaused, now: now.toISOString() });
            }

            const elapsedMs = Math.max(0, now - start);
            const remainingMs = Math.max(0, end - now);
            //remainingMs = 0;

            // 🔥 NEW LOGIC: Check if time has run out
            if (remainingMs <= 0 && !hasTimedOut.current) {
                hasTimedOut.current = true; // Mark as timed out so it doesn't loop
                clearInterval(interval);    // Stop the clock
                exitFullScreen();           // Close the map if it's open
                setInputValue("");          // Clear any typed answers
                setOverlayType('timeout');  // Show the timeout overlay
                setOverlayVisible(true);
            }

            const formatTime = (ms) => {
                const totalSeconds = Math.floor(ms / 1000);
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                return `${hours}h ${minutes}m ${seconds}s`;
            };

            setElapsedTime(formatTime(elapsedMs));
            setRemainingTime(formatTime(remainingMs));
        }, 1000);

        return () => clearInterval(interval);
    }, [step?.start_date, step?.end_date, step?.is_paused, step?.paused_at, isPaused]);

    const toggleTimeView = () => setShowRemaining((prev) => !prev);



    if (!step) {
        return <div>{t('loading')}...</div>;
    }

    if (!imagePath) {
        // Optionally render a placeholder or nothing when the image path is not available
        return null;
    }

    return (
        <div className="your-component" style={{ position: 'relative', overflow: 'hidden' }}>
            <div
                className="background"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${imagePath})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(2px)', // Adjust blur level
                    zIndex: -1,
                }}
            ></div>


            <div className="content-container">
                {/* Text description */}

                {/* Timer in top-left corner */}
                {step && step.start_date && step.end_date && overlayType !== 'map' && (
                    <div
                        className="timer-box"
                        onClick={toggleTimeView}
                        style={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            padding: '10px 15px',
                            borderRadius: '10px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            zIndex: 10,
                            userSelect: 'none',
                        }}
                    >
                        {showRemaining
                            ? `⏳ ${t('remaining_time')}: ${remainingTime}`
                            : `🕒 ${t('elapsed_time')}: ${elapsedTime}`}
                    </div>
                )}

                {/* Step progress in bottom-left corner */}
                {step && step.step !== undefined && step.total_steps !== undefined && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 10,
                            left: 10,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            padding: '8px 12px',
                            borderRadius: '10px',
                            fontSize: '20px',
                            zIndex: 10,
                        }}
                    >
                        {step.step} / {step.total_steps}
                    </div>
                )}

                {/* Input field and button centered at the bottom */}
                <div className="input-container">
                    <h1>{step?.text}</h1>


                    <input
                        type="text"
                        placeholder={t('enter_something')}
                        value={inputValue}
                        onChange={handleInputChange}
                    />
                    <button onClick={() => handleNextStep()}>{t('submit')}</button>
                </div>



                {/* Icons on the right side */}
                <div className="icon-container">
                    {/* Icon 1 */}
                    <div className="icon" onClick={() => handleIconClick('hints')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f6d365', color: 'white', fontSize: '24px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', cursor: 'pointer' }}>
                        <FaLightbulb />
                    </div>

                    {/* Icon 2 */}
                    <div className="icon" onClick={() => handleIconClick('map')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4facfe', color: 'white', fontSize: '24px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', cursor: 'pointer' }}>
                        <FaMapMarkedAlt />
                    </div>

                    {step.skip_step === 1 && (
                        <div className="icon" onClick={() => handleIconClick('skip')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#a18cd1', color: 'white', fontSize: '24px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', cursor: 'pointer' }}>
                            <FaFastForward />
                        </div>
                    )}
                </div>

                {/* Introduction Guide */}
                {showIntroGuide && (
                    <IntroGuideOverlay
                        hasSkip={step.skip_step === 1}
                        onClose={() => {
                            localStorage.setItem(`intro_shown_${playId}`, 'true');
                            setShowIntroGuide(false);
                        }}
                    />
                )}

                {/* Overlay view */}
                {overlayVisible && (
                    <div className={` ${overlayType === 'map' ? 'map-overlay-steps' : 'overlay-steps'}`}>
                        {/* 🔥 ADD THE CONFETTI HERE 🔥 */}
                        {overlayType === 'final' && (
                            <Confetti
                                width={windowDimension.width}
                                height={windowDimension.height}
                                numberOfPieces={400} /* Makes it a dense, exciting burst! */
                                gravity={0.15} /* Makes it fall nicely */
                                style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }} /* Ensures it covers everything */
                            />
                        )}

                        <div className={` ${overlayType === 'map' ? 'overlay-content-map' : 'overlay-content'}`}>
                            {overlayType === 'hints' && (
                                <div>
                                    <h2>{t('hints')}</h2>
                                    <p2>{step.hint1}</p2>
                                    <br />
                                    {step?.show_step_regions === 1 && (
                                        <p style={{ marginTop: '15px', marginBottom: '15px', color: '#666' }}>{t('confirm_hint_map_text')}</p>
                                    )}
                                    <button onClick={() => closeOverlay("hints")}>{t('close')}</button>
                                </div>
                            )}
                            {overlayType === 'hint_confirm' && (
                                <div>
                                    <h2>{t('confirm_hint_title')}</h2>
                                    {step?.max_hints !== null && step?.max_hints !== undefined && (
                                        <p style={{ marginBottom: '15px' }}>
                                            {t('hints_remaining')} {step.max_hints - step.used_hints}
                                        </p>
                                    )}
                                    
                                    {(!step?.max_hints || (step.max_hints - step.used_hints > 0)) ? (
                                        <button onClick={async () => {
                                            const gameContentProvider = new GameContentProvider();
                                            await gameContentProvider.useHint(playId, step.step);
                                            setStep(prev => ({ ...prev, hint_used_this_step: 1, used_hints: prev.used_hints + 1 }));
                                            setHintOpened(true);
                                            setOverlayType('hints');
                                        }} style={{ marginRight: '10px' }}>{t('yes')}</button>
                                    ) : (
                                        <p style={{ color: 'red', marginBottom: '15px' }}>{t('no_hints_remaining')}</p>
                                    )}
                                    
                                    <button onClick={() => closeOverlay("hints")}>{t('cancel')}</button>
                                </div>
                            )}
                            {overlayType === 'skip' && (
                                <div>
                                    <h2>{t('confirm_skip')}</h2>
                                    <button onClick={() => skipStep()}>{t('skip')}</button>
                                    <button onClick={() => closeOverlay("hints")}>{t('close')}</button>
                                </div>
                            )}

                            {overlayType === 'skipped' && (
                                <div style={{ textAlign: 'center' }}>
                                    <h2 style={{ fontSize: '26px', marginBottom: '10px' }}>{t('skipped_title')}</h2>
                                    <p style={{ fontSize: '15px', color: '#666', marginBottom: '25px' }}>
                                        {t('skipped_desc')}
                                    </p>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', margin: '0 auto 24px' }}>
                                        {step?.audio_base && (
                                            <div
                                                onClick={toggleAudio}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                                    cursor: 'pointer',
                                                    color: '#4facfe', fontSize: '14px', fontWeight: 500, textAlign: 'center', maxWidth: '120px'
                                                }}
                                            >
                                                <span style={{ fontSize: '32px' }}>
                                                    {isAudioPlaying ? <FaPause /> : <FaPlay />}
                                                </span>
                                                {!isAudioPlaying && <span>{t('audio_facts')}</span>}
                                            </div>
                                        )}
                                        {step?.images_count > 0 && (
                                            <div
                                                onClick={() => {
                                                    setCarouselIndex(1);
                                                    setCarouselVisible(true);
                                                }}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                                    cursor: 'pointer',
                                                    color: '#ff9a9e', fontSize: '14px', fontWeight: 500, textAlign: 'center', maxWidth: '120px'
                                                }}
                                            >
                                                <span style={{ fontSize: '32px' }}>
                                                    <FaImages />
                                                </span>
                                                <span>{t('view_images') || "View Images"}</span>
                                            </div>
                                        )}
                                    </div>
                                    {step.guide && (
                                        <p className="step-guide-text">{step.guide}</p>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                        <button
                                            style={{ flex: 1, padding: '12px 5px', fontSize: '16px', margin: 0 }}
                                            onClick={() => {
                                                stopAudio();
                                                setOverlayVisible(false);
                                                setOverlayType(null);
                                                if (skipNextData) {
                                                    navigate(location.pathname, { state: skipNextData, replace: true });
                                                }
                                            }}
                                        >
                                            {t('continue')}
                                        </button>
                                        <button
                                            onClick={() => handlePauseFromCorrect('skipped')}
                                            style={{ flex: 1, padding: '12px 5px', fontSize: '16px', margin: 0, backgroundColor: '#ff9a9e', color: 'white' }}
                                        >
                                            {t('pause')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {overlayType === 'map' &&
                                <MapOverlay
                                    closeOverlay={closeOverlay}
                                    recommendations={step.recommendations}
                                    city={step.city}
                                    step={step}
                                    hintOpened={hintOpened}
                                />
                            }

                            {overlayType === 'sync' && (
                                <div>
                                    <h2>{t('game_synced') || "Game Synced!"}</h2>
                                    <p style={{ marginBottom: '20px' }}>
                                        {t('teammate_advanced_step') || "A teammate completed this step! Moving to the next one..."}
                                    </p>
                                    <button onClick={() => {
                                        // 1. Close overlay
                                        setOverlayVisible(false);
                                        setOverlayType(null);

                                        // 2. Navigate to the new step
                                        const newData = {
                                            game: gameId,
                                            step: syncTargetStep,
                                            play_id: playId,
                                        };
                                        navigate(location.pathname, { state: newData, replace: true });
                                    }}>
                                        {t('go_to_next_step') || "Continue"}
                                    </button>
                                </div>
                            )}


                            {overlayType === 'wrong' && (
                                <div>
                                    <h2>{t('code_is_incorrect')}</h2>
                                    <button onClick={() => closeOverlay("wrong")}>{t('close')}</button>
                                </div>
                            )}

                            {overlayType === 'correct' && (
                                <div>
                                    <h2>{t('correct')} !</h2>

                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', margin: '0 auto 24px' }}>
                                        {step?.audio_base && (
                                            <div
                                                onClick={toggleAudio}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                                    cursor: 'pointer',
                                                    color: '#4facfe', fontSize: '14px', fontWeight: 500, textAlign: 'center', maxWidth: '120px'
                                                }}
                                            >
                                                <span style={{ fontSize: '32px' }}>
                                                    {isAudioPlaying ? <FaPause /> : <FaPlay />}
                                                </span>
                                                {!isAudioPlaying && <span>{t('audio_facts')}</span>}
                                            </div>
                                        )}
                                        {step?.images_count > 0 && (
                                            <div
                                                onClick={() => {
                                                    setCarouselIndex(1);
                                                    setCarouselVisible(true);
                                                }}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                                    cursor: 'pointer',
                                                    color: '#ff9a9e', fontSize: '14px', fontWeight: 500, textAlign: 'center', maxWidth: '120px'
                                                }}
                                            >
                                                <span style={{ fontSize: '32px' }}>
                                                    <FaImages />
                                                </span>
                                                <span>{t('view_images') || "View Images"}</span>
                                            </div>
                                        )}


                                    </div>
                                    {step.guide && (
                                        <p className="step-guide-text">{step.guide}</p>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                        <button
                                            style={{ flex: 1, padding: '12px 5px', fontSize: '16px', margin: 0 }}
                                            onClick={() => closeOverlay("correct")}
                                        >
                                            {t('continue')}
                                        </button>
                                        <button
                                            onClick={() => handlePauseFromCorrect("correct")}
                                            style={{ flex: 1, padding: '12px 5px', fontSize: '16px', margin: 0, backgroundColor: '#ff9a9e', color: 'white' }}
                                        >
                                            {t('pause')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* TIME UP OVERLAY */}
                            {overlayType === 'timeout' && (
                                <div>
                                    <h2>⏰ {t('time_is_up') || "Time is up!"}</h2>
                                    <p style={{ marginBottom: '20px' }}>
                                        {t('game_over_time') || "You have run out of time for this game."}
                                    </p>
                                    <button onClick={() => {
                                        setOverlayVisible(false);
                                        setOverlayType(null);

                                        // Navigate to the game overview page
                                        const newData = {
                                            game: gameId,
                                            step: stepId,
                                            play_id: playId,
                                        };
                                        navigate("/game-overview", { state: newData, replace: true });
                                    }}>
                                        {t('go_to_overview') || "View Results"}
                                    </button>
                                </div>
                            )}


                            {overlayType === 'final' && (
                                <div>
                                    <h2>🎉 {t('correct')} ! 🎉</h2>
                                    <p style={{ marginBottom: '20px' }}>{t('congratulations_on_completing_the_game')}</p>

                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', margin: '0 auto 24px' }}>
                                        {step?.audio_base && (
                                            <div
                                                onClick={toggleAudio}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                                    cursor: 'pointer',
                                                    color: '#4facfe', fontSize: '14px', fontWeight: 500, textAlign: 'center', maxWidth: '120px'
                                                }}
                                            >
                                                <span style={{ fontSize: '32px' }}>
                                                    {isAudioPlaying ? <FaPause /> : <FaPlay />}
                                                </span>
                                                {!isAudioPlaying && <span>{t('audio_facts')}</span>}
                                            </div>
                                        )}
                                        {step?.images_count > 0 && (
                                            <div
                                                onClick={() => {
                                                    setCarouselIndex(1);
                                                    setCarouselVisible(true);
                                                }}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                                    cursor: 'pointer',
                                                    color: '#ff9a9e', fontSize: '14px', fontWeight: 500, textAlign: 'center', maxWidth: '120px'
                                                }}
                                            >
                                                <span style={{ fontSize: '32px' }}>
                                                    <FaImages />
                                                </span>
                                                <span>{t('view_images') || "View Images"}</span>
                                            </div>
                                        )}
                                    </div>
                                    {step.guide && (
                                        <p className="step-guide-text">{step.guide}</p>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                        <button style={{ width: '100%', margin: 0 }} onClick={() => closeOverlay("final")}>{t('close')}</button>
                                    </div>
                                </div>
                            )}

                            {overlayType === 'paused' && (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '60px', color: '#ff9a9e', marginBottom: '20px' }}>
                                        <FaPlay onClick={handleResume} style={{ cursor: 'pointer' }} />
                                    </div>
                                    <h2 style={{ fontSize: '28px', marginBottom: '15px' }}>{t('game_paused')}</h2>
                                    <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.5', marginBottom: '25px' }}>
                                        {t('game_paused_desc')}
                                    </p>
                                    <button
                                        className="resume-button"
                                        onClick={handleResume}
                                        style={{
                                            padding: '12px 30px',
                                            fontSize: '18px',
                                            backgroundColor: '#ff9a9e',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '25px',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 15px rgba(255, 154, 158, 0.4)',
                                            transition: 'transform 0.2s'
                                        }}
                                    >
                                        {t('continue')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/* Full-screen 3D Carousel Overlay */}
            {carouselVisible && step?.images_base && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(5px)', zIndex: 10000,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                }}>
                    <FaTimes
                        onClick={() => setCarouselVisible(false)}
                        style={{ position: 'absolute', top: '20px', right: '20px', color: 'white', fontSize: '30px', cursor: 'pointer', zIndex: 10001 }}
                    />

                    <div
                        style={{
                            position: 'relative', width: '100%', flex: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden'
                        }}
                        onTouchStart={(e) => {
                            setTouchEndX(null);
                            setTouchStartX(e.targetTouches[0].clientX);
                        }}
                        onTouchMove={(e) => setTouchEndX(e.targetTouches[0].clientX)}
                        onTouchEnd={() => {
                            if (!touchStartX || !touchEndX) return;
                            const distance = touchStartX - touchEndX;
                            const isLeftSwipe = distance > 50;
                            const isRightSwipe = distance < -50;

                            if (isLeftSwipe) {
                                setCarouselIndex(prev => (prev < step.images_count ? prev + 1 : prev));
                            }
                            if (isRightSwipe) {
                                setCarouselIndex(prev => (prev > 1 ? prev - 1 : prev));
                            }
                        }}
                    >
                        {Array.from({ length: step.images_count }).map((_, idx) => {
                            const i = idx + 1;
                            const offset = i - carouselIndex;
                            let transform = '';
                            let opacity = 0;
                            let zIndex = 1;
                            let isClickable = true;

                            if (offset === 0) {
                                transform = 'translateX(0) scale(1)';
                                opacity = 1;
                                zIndex = 10;
                                isClickable = false;
                            } else if (offset === -1) {
                                transform = 'translateX(-70%) scale(0.75)';
                                opacity = 0.5;
                                zIndex = 5;
                            } else if (offset === 1) {
                                transform = 'translateX(70%) scale(0.75)';
                                opacity = 0.5;
                                zIndex = 5;
                            } else {
                                transform = `translateX(${offset > 0 ? 150 : -150}%) scale(0.5)`;
                                opacity = 0;
                                zIndex = 1;
                                isClickable = false;
                            }

                            return (
                                <div
                                    key={i}
                                    style={{
                                        position: 'absolute',
                                        width: '75%',
                                        maxWidth: '450px',
                                        height: '60vh',
                                        transition: 'all 0.4s ease-in-out',
                                        transform: transform,
                                        opacity: opacity,
                                        zIndex: zIndex,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: isClickable ? 'pointer' : 'default'
                                    }}
                                    onClick={() => {
                                        if (isClickable) setCarouselIndex(i);
                                    }}
                                >
                                    <img
                                        src={`/assets/step_images/${step.images_base}_${i}.jpg`}
                                        alt={`Slide ${i}`}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            borderRadius: '15px',
                                            boxShadow: offset === 0 ? '0 15px 35px rgba(0,0,0,0.5)' : '0 5px 15px rgba(0,0,0,0.3)',
                                        }}
                                    />
                                </div>
                            );
                        })}

                        {carouselIndex > 1 && (
                            <div
                                onClick={() => setCarouselIndex(prev => prev - 1)}
                                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 10001, cursor: 'pointer', padding: '10px' }}
                            >
                                <FaChevronLeft style={{ color: 'white', fontSize: '40px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                            </div>
                        )}

                        {carouselIndex < step.images_count && (
                            <div
                                onClick={() => setCarouselIndex(prev => prev + 1)}
                                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 10001, cursor: 'pointer', padding: '10px' }}
                            >
                                <FaChevronRight style={{ color: 'white', fontSize: '40px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                            </div>
                        )}
                    </div>

                    <div style={{ padding: '20px', color: 'white', textAlign: 'center', minHeight: '100px', width: '100%', maxWidth: '600px', fontSize: '16px', lineHeight: '1.5', zIndex: 10001 }}>
                        {t(`${step.images_base}_${carouselIndex}`)}
                    </div>
                </div>
            )}
        </div>
    );


};

export default GameSteps;




